import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import VisuallyHidden from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"
import { matchesPath, resolveActiveIcon } from "./navigation-utils"

// =============================================================================
// Props
// =============================================================================

export type NavigationDockActionProps = {
  icon: IconName
  label: string
  /** Pathname prefix used to derive active state. Matches via `startsWith`. */
  pathPrefix?: string
  /** Swap the rendered element — e.g. pass `<Link href="..." />` for routing. */
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const actionStyles = [
  "pointer-events-auto",
  "flex items-center justify-center shrink-0",
  "h-10 w-10 rounded-full",
  "text-gray-700",
  "hover:bg-gray-900/[0.06]",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "transition-colors",
  "data-active:text-gray-900"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * A single navigation link inside `Navigation.Dock.Actions`.
 * Icon-only on screen; `label` is read by screen readers.
 */
export function NavigationDockAction({ icon, label, pathPrefix, render }: NavigationDockActionProps) {
  const { currentPath } = useNavigationContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  return useRender({
    defaultTagName: "button",
    render,
    props: {
      type: "button" as const,
      "data-slot": "navigation-dock-action",
      "data-active": isActive ? "" : undefined,
      "aria-current": isActive ? ("page" as const) : undefined,
      className: actionStyles,
      children: (
        <>
          <Icon name={isActive ? resolveActiveIcon(icon) : icon} aria-hidden />
          <VisuallyHidden>{label}</VisuallyHidden>
        </>
      )
    }
  })
}

NavigationDockAction.displayName = "Navigation.Dock.Action"
